const EASE_OUT_EXPO = "cubic-bezier(0.19, 1, 0.22, 1)";
const ENTRANCE_DURATION_MS = 800;
const HEADER_LOADED_KEY = "theme-header-loaded";
const ENTRANCE_SCALE = 0.98;
/** @type {Window & { themeAfterPageReveal?: (fn: () => void) => void }} */
const themeWindow = window;

/**
 * @param {() => void} fn
 */
function scheduleLoadAnimation(fn) {
  if (typeof themeWindow.themeAfterPageReveal === "function") {
    themeWindow.themeAfterPageReveal(fn);
    return;
  }

  const run = () => requestAnimationFrame(() => requestAnimationFrame(fn));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
}

function dispatchLoadAnimationsStart() {
  document.dispatchEvent(new CustomEvent("theme:load-animations-start"));
}

function markHeaderLoaded() {
  try {
    sessionStorage.setItem(HEADER_LOADED_KEY, "true");
  } catch {
    /* ignore */
  }
}

function hasLoadedHeaderBefore() {
  try {
    return sessionStorage.getItem(HEADER_LOADED_KEY) === "true";
  } catch {
    return false;
  }
}

function isManualReload() {
  const nav = performance.getEntriesByType("navigation")[0];
  return nav?.type === "reload";
}

function shouldPlayHeaderEntranceAnimation() {
  if (isManualReload()) return true;
  return !hasLoadedHeaderBefore();
}

/**
 * @param {HTMLElement} headerComponent
 */
function getEntranceTarget(headerComponent) {
  return headerComponent;
}

/**
 * @param {HTMLElement} target
 */
function resetEntranceTargetStyles(target) {
  target.style.removeProperty("transform");
  target.style.removeProperty("scale");
  target.style.removeProperty("transform-origin");
  target.style.removeProperty("will-change");
}

/**
 * @param {HTMLElement} headerSection
 * @param {HTMLElement} headerComponent
 */
function revealHeaderWithoutEntrance(headerSection, headerComponent) {
  headerSection.dataset.loadAnimationReady = "true";
  headerSection.dataset.loadAnimated = "true";
  resetEntranceTargetStyles(getEntranceTarget(headerComponent));
  dispatchLoadAnimationsStart();
}

/**
 * @param {HTMLElement} target
 */
function runEntranceScaleAnimation(target) {
  resetEntranceTargetStyles(target);
  target.style.transformOrigin = "center center";
  target.style.transform = `scale(${ENTRANCE_SCALE})`;

  const animation = target.animate(
    [
      { transform: `scale(${ENTRANCE_SCALE})` },
      { transform: "scale(1)" },
    ],
    {
      duration: ENTRANCE_DURATION_MS,
      easing: EASE_OUT_EXPO,
      fill: "both",
    },
  );

  return animation.finished
    .then(() => {
      resetEntranceTargetStyles(target);
      animation.cancel();
    })
    .catch(() => {
      resetEntranceTargetStyles(target);
    });
}

function runHeaderLoadAnimation() {
  const headerComponent = document.querySelector("#header-component[sticky]");
  const headerSection = headerComponent?.closest(".header-section");
  if (!(headerSection instanceof HTMLElement)) {
    dispatchLoadAnimationsStart();
    return;
  }

  if (!(headerComponent instanceof HTMLElement)) {
    dispatchLoadAnimationsStart();
    return;
  }

  if (headerSection.dataset.loadAnimated === "true") {
    dispatchLoadAnimationsStart();
    return;
  }

  const entranceTarget = getEntranceTarget(headerComponent);

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    headerSection.dataset.loadAnimationReady = "true";
    headerSection.dataset.loadAnimated = "true";
    resetEntranceTargetStyles(entranceTarget);
    markHeaderLoaded();
    dispatchLoadAnimationsStart();
    return;
  }

  if (!shouldPlayHeaderEntranceAnimation()) {
    revealHeaderWithoutEntrance(headerSection, headerComponent);
    return;
  }

  headerSection.dataset.loadAnimated = "true";

  headerSection.style.visibility = "visible";
  void headerSection.offsetHeight;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      headerSection.dataset.loadAnimationReady = "true";
      headerSection.style.removeProperty("visibility");

      dispatchLoadAnimationsStart();

      runEntranceScaleAnimation(entranceTarget)
        .then(() => {
          markHeaderLoaded();
          document.dispatchEvent(new CustomEvent("theme:header-load-complete"));
        })
        .catch(() => {
          markHeaderLoaded();
        });
    });
  });
}

if (shouldPlayHeaderEntranceAnimation()) {
  scheduleLoadAnimation(runHeaderLoadAnimation);
} else if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", runHeaderLoadAnimation, {
    once: true,
  });
} else {
  runHeaderLoadAnimation();
}
